'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAccount } from '@/hooks/useAccount';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheckDouble } from '@fortawesome/pro-duotone-svg-icons';
import Link from 'next/link';
import TimeAgo from 'timeago-react';
import useClickOutside from '@/hooks/useClickOutside';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NotificationState, Notification } from '@/actions/dtos/notification';
import { fetchNotifications, markNotificationsAsSeen, updateNotificationState } from '@/api/requests/notifications';
import '@/lib/timeago/custom-de-locale';

const NotificationsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { person } = useAccount();
  const PAGE_SIZE = 5;

  const loadNotifications = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!person?.id || loading) return;

      setLoading(true);
      try {
        if (pageNum === 0 && !append) {
          const unseenResponse = await fetchNotifications(person.id, 0, 0, NotificationState.UNSEEN);
          setUnseenCount(unseenResponse.totalElements);
        }

        const response = await fetchNotifications(person.id, pageNum, PAGE_SIZE);
        const fetchedNotifications = response.content as Notification[];

        setNotifications((prev) => (append ? [...prev, ...fetchedNotifications] : fetchedNotifications));

        setHasMore(!response.last);
      } finally {
        setLoading(false);
      }
    },
    [person?.id, loading],
  );

  const fetchMoreData = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadNotifications(nextPage, true);
    }
  }, [loading, hasMore, page, loadNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    const newState = notification.url ? NotificationState.OPENED : NotificationState.SEEN;

    if (notification.state !== newState) {
      await updateNotificationState(notification.id, newState);
      setNotifications((prev) =>
        prev.map((n: Notification) => (n.id === notification.id ? { ...n, state: newState } : n)),
      );

      if (notification.state === NotificationState.UNSEEN) {
        setUnseenCount((prevCount) => prevCount - 1);
      }
    }

    if (notification.url) {
      setIsOpen(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    await markNotificationsAsSeen();

    setNotifications((prev) =>
      prev.map((n: Notification) =>
        n.state === NotificationState.UNSEEN ? { ...n, state: NotificationState.SEEN } : n,
      ),
    );

    setUnseenCount(0);
  };

  const toggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  };

  useEffect(() => {
    if (person?.id) {
      loadNotifications(0, false);
    }
  }, [person?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside(dropdownRef, closeDropdown);

  const renderNotificationContent = (notification: Notification) => {
    const notificationContent = (
      <div className="flex items-start">
        <div className="min-w-0 flex-grow">
          <div className="mb-1 flex w-full flex-col xs:flex-row">
            <div className="min-w-0 flex-grow pr-2">
              <h4 className="break-words font-medium">{notification.title}</h4>
            </div>
            <div className="mt-1 flex flex-shrink-0 items-center gap-2 xs:mt-0">
              <span className="whitespace-nowrap text-xs text-primary">
                <TimeAgo datetime={notification.created} locale="de-custom" opts={{ minInterval: 60 }} />
              </span>
              {notification.state === NotificationState.UNSEEN && (
                <span className="h-2 w-2 rounded-full bg-accent"></span>
              )}
            </div>
          </div>
          <p className="break-words text-sm text-primary">{notification.message}</p>
        </div>
      </div>
    );

    if (!notification.url) {
      return (
        <div
          onClick={() => handleNotificationClick(notification)}
          className="block cursor-pointer border-b p-3 hover:bg-base-200"
        >
          {notificationContent}
        </div>
      );
    }

    return (
      <Link
        target="_blank"
        key={notification.id}
        href={notification.url}
        onClick={() => handleNotificationClick(notification)}
        className="block border-b p-3 hover:bg-base-200"
      >
        {notificationContent}
      </Link>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative flex items-center justify-center rounded-full p-1 text-primary"
        aria-label="Benachrichtigungen"
      >
        <FontAwesomeIcon icon={faBell} style={{ fontSize: '20px' }} />
        {unseenCount > 0 && (
          <span className="absolute right-[-4px] top-[-4px] flex h-4 w-4 items-center justify-center rounded-sm bg-accent text-xs text-base-100">
            {unseenCount > 9 ? '9+' : unseenCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          id="notificationsDropdown"
          className="fixed inset-x-0 z-[100] mx-auto mt-2 max-h-96 w-11/12 rounded border border-base-200 bg-base-100 xs:w-5/6 md:absolute md:inset-x-auto md:right-0 md:mx-0 md:w-[480px]"
        >
          <div className="flex items-center justify-between border-b p-3">
            <h3 className="hidden font-medium sm:inline">Benachrichtigungen ({unseenCount})</h3>
            <button
              onClick={handleMarkAllAsRead}
              disabled={unseenCount === 0}
              className={`flex items-center gap-1 text-xs text-primary xs:text-sm ${
                unseenCount > 0 ? 'lg:hover:underline' : 'cursor-not-allowed opacity-60'
              }`}
            >
              <FontAwesomeIcon icon={faCheckDouble} size="sm" />
              <span className="font-semibold">Alle als gelesen markieren</span>
            </button>
          </div>

          <div className="overflow-y-auto" id="scrollableNotifications" style={{ maxHeight: 'calc(24rem - 50px)' }}>
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-base-200">Keine Benachrichtigungen</div>
            ) : (
              <InfiniteScroll
                dataLength={notifications.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={<div className="p-2 text-center text-base-200">Wird geladen...</div>}
                scrollableTarget="scrollableNotifications"
                className="overflow-visible"
              >
                {notifications.map((notification) => renderNotificationContent(notification))}
              </InfiniteScroll>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
